import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config';
import io from 'socket.io-client';
import { colors, commonStyles } from '../theme';
import { getToken } from '../auth';
import { LANGUAGES } from '../i18n';

function formatSelectedAnswer(answer) {
  if (answer.question_type === 'open') {
    return answer.answer_text || '—';
  }
  if (answer.selected_answers_json) {
    try {
      const selected = JSON.parse(answer.selected_answers_json);
      if (Array.isArray(selected) && selected.length > 0) {
        return selected.join(', ');
      }
    } catch {
      // fall back to selected_answer
    }
  }
  return answer.selected_answer || '—';
}

function formatScore(score) {
  const numeric = Number(score || 0);
  return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(2);
}

function OrganizerDashboard() {
  const [quizzes, setQuizzes] = useState([]);
  const [selectedQuizId, setSelectedQuizId] = useState(null);
  const [teams, setTeams] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [toast, setToast] = useState(null);


  const [copyingRoundId, setCopyingRoundId] = useState(null);
  const [copyTargetQuizId, setCopyTargetQuizId] = useState('');

  // Answer grading
  const [gradingRoundId, setGradingRoundId] = useState(null);
  const [roundAnswers, setRoundAnswers] = useState([]);

  // Quiz creation form
  const [showCreateQuiz, setShowCreateQuiz] = useState(false);
  const [newQuizName, setNewQuizName] = useState('');
  const [newQuizCode, setNewQuizCode] = useState('');

  // Quiz editing (rename / change access code / language)
  const [editingQuiz, setEditingQuiz] = useState(false);
  const [editQuizName, setEditQuizName] = useState('');
  const [editQuizCode, setEditQuizCode] = useState('');
  const [editQuizLanguage, setEditQuizLanguage] = useState('en');

  // Saved team-facing screens (opening / break / end / messages)
  const [screens, setScreens] = useState([]);
  const [editingScreenId, setEditingScreenId] = useState(null); // id, or 'new'
  const [screenTitleDraft, setScreenTitleDraft] = useState('');
  const [screenBodyDraft, setScreenBodyDraft] = useState('');
  // Always one blank row at the end to type into, dropped on save.
  const [screenLinksDraft, setScreenLinksDraft] = useState([{ label: '', url: '' }]);
  const [screenCountdownDraft, setScreenCountdownDraft] = useState(''); // minutes, as typed

  // Who handed in, for one round. Fetched on demand, not polled: the socket
  // already pushes 'team:progress' whenever a team touches an answer.
  const [handInsRoundId, setHandInsRoundId] = useState(null);
  const [handIns, setHandIns] = useState([]);

  // Team editing
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [teamNameDraft, setTeamNameDraft] = useState('');

  // The socket effect runs once per quiz, so its handlers can't see later
  // state. This ref lets them refresh whichever round's list is open.
  const handInsRoundIdRef = useRef(null);
  useEffect(() => { handInsRoundIdRef.current = handInsRoundId; }, [handInsRoundId]);
  // What teams are looking at right now, as far as this dashboard knows.
  // Purely a UI affordance so you can see which button is "live".
  const [liveDisplay, setLiveDisplay] = useState(null); // {type:'screen',id} | {type:'leaderboard',mode}

  // Round question list (expandable dropdown) + round renaming
  const [allQuestions, setAllQuestions] = useState([]);
  const [expandedRoundIds, setExpandedRoundIds] = useState([]);
  const [editingRoundId, setEditingRoundId] = useState(null);
  const [roundNameDraft, setRoundNameDraft] = useState('');

  const showToast = (message, duration = 3000) => {
    setToast(message);
    setTimeout(() => setToast(null), duration);
  };

  // Fetch quizzes on mount
  useEffect(() => {
    fetchQuizzes();
  }, []);

  // Connect socket and fetch quiz data when a quiz is selected
  useEffect(() => {
    if (!selectedQuizId) return;

    const newSocket = io(API_URL);
    setSocket(newSocket);

    newSocket.emit('organizer:join', { quizId: selectedQuizId, token: getToken() });

    newSocket.on('organizer:joined', (data) => {
      console.log('Organizer connected:', data);
    });

    newSocket.on('team:answered', (data) => {
      console.log('Team answered:', data);
      fetchLeaderboard(selectedQuizId);
      // A hand-in changes who is still outstanding.
      if (handInsRoundIdRef.current) fetchHandIns(handInsRoundIdRef.current);
    });

    // A team touched an answer without handing in. Only the hand-ins list
    // cares; the leaderboard must not move, because drafts don't score.
    newSocket.on('team:progress', () => {
      if (handInsRoundIdRef.current) fetchHandIns(handInsRoundIdRef.current);
    });

    newSocket.on('organizer:roundActivated', (data) => {
      showToast('Round activated! All teams received questions.');
      fetchRounds(selectedQuizId);
      // The server drops any screen or leaderboard when a round goes live.
      setLiveDisplay(null);
    });

    newSocket.on('organizer:roundClosed', (data) => {
      const pending = (data && data.pendingDrafts) || 0;
      showToast(
        pending > 0
          ? `Round closed. ${pending} unsubmitted ${pending === 1 ? 'answer is' : 'answers are'} waiting on you.`
          : 'Round closed! Teams can no longer submit answers.',
        pending > 0 ? 6000 : 3000
      );
      fetchRounds(selectedQuizId);
      // Open the hand-ins list straight away when something needs deciding,
      // so unsubmitted answers can't be forgotten about silently.
      if (data && data.roundId) fetchHandIns(data.roundId);
    });

    newSocket.on('organizer:roundReopened', (data) => {
      showToast('Round reopened! Teams can submit answers again.');
      fetchRounds(selectedQuizId);
    });

    fetchTeams(selectedQuizId);
    fetchRounds(selectedQuizId);
    fetchLeaderboard(selectedQuizId);
    fetchAllQuestions(selectedQuizId);
    fetchScreens(selectedQuizId);

    return () => {
      newSocket.disconnect();
    };
    // Deliberately keyed to the selected quiz alone. The fetch helpers are
    // recreated on every render, so listing them would tear the socket down
    // and reconnect constantly; the handlers read changing values through
    // refs instead (see handInsRoundIdRef).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuizId]);

  const fetchQuizzes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/organizer/quizzes`);
      const data = await response.json();
      setQuizzes(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
      setLoading(false);
    }
  };

  const fetchTeams = async (quizId) => {
    try {
      const response = await fetch(`${API_URL}/api/organizer/quiz/${quizId}/teams`);
      const data = await response.json();
      setTeams(data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchRounds = async (quizId) => {
    try {
      const response = await fetch(`${API_URL}/api/organizer/quiz/${quizId}/rounds`);
      const data = await response.json();
      setRounds(data);
    } catch (error) {
      console.error('Error fetching rounds:', error);
    }
  };

  const fetchLeaderboard = async (quizId) => {
    try {
      const response = await fetch(`${API_URL}/api/organizer/quiz/${quizId}/leaderboard`);
      const data = await response.json();
      setLeaderboard(data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const fetchAllQuestions = async (quizId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/questions?quiz_id=${quizId}`);
      const data = await response.json();
      setAllQuestions(data);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const fetchScreens = async (quizId) => {
    try {
      const response = await fetch(`${API_URL}/api/organizer/quiz/${quizId}/screens`);
      const data = await response.json();
      setScreens(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching screens:', error);
    }
  };

  // Links always carry a trailing blank row so there is somewhere to type.
  const withBlankRow = (links) => [...links, { label: '', url: '' }];

  const startNewScreen = () => {
    setEditingScreenId('new');
    setScreenTitleDraft('');
    setScreenBodyDraft('');
    setScreenLinksDraft([{ label: '', url: '' }]);
    setScreenCountdownDraft('');
  };

  const startEditScreen = (screen) => {
    setEditingScreenId(screen.id);
    setScreenTitleDraft(screen.title || '');
    setScreenBodyDraft(screen.body || '');
    setScreenLinksDraft(withBlankRow(
      (screen.links || []).map((l) => ({ label: l.label || '', url: l.url || '' }))
    ));
    // Stored in seconds, edited in minutes: nobody sets a break in seconds.
    setScreenCountdownDraft(
      screen.countdown_seconds ? String(Math.round(screen.countdown_seconds / 60)) : ''
    );
  };

  const cancelScreenEdit = () => {
    setEditingScreenId(null);
    setScreenTitleDraft('');
    setScreenBodyDraft('');
    setScreenLinksDraft([{ label: '', url: '' }]);
    setScreenCountdownDraft('');
  };

  const changeScreenLink = (index, field, value) => {
    setScreenLinksDraft((prev) => {
      const next = prev.map((l, i) => (i === index ? { ...l, [field]: value } : l));
      // Typing in the last row grows the list by one more blank row.
      const last = next[next.length - 1];
      if (last && (last.label.trim() || last.url.trim())) return withBlankRow(next);
      return next;
    });
  };

  const removeScreenLink = (index) => {
    setScreenLinksDraft((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [{ label: '', url: '' }];
    });
  };

  const saveScreen = async () => {
    if (!screenTitleDraft.trim()) {
      showToast('Give the screen a title');
      return;
    }
    const isNew = editingScreenId === 'new';
    const url = isNew
      ? `${API_URL}/api/organizer/quiz/${selectedQuizId}/screens`
      : `${API_URL}/api/organizer/screens/${editingScreenId}`;

    const minutes = screenCountdownDraft.trim();
    if (minutes && !/^\d+$/.test(minutes)) {
      showToast('Countdown must be a whole number of minutes');
      return;
    }

    try {
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: screenTitleDraft.trim(),
          body: screenBodyDraft,
          links: screenLinksDraft.filter((l) => l.label.trim() || l.url.trim()),
          countdown_seconds: minutes ? Number(minutes) * 60 : null,
        }),
      });
      if (response.ok) {
        fetchScreens(selectedQuizId);
        cancelScreenEdit();
      } else {
        const data = await response.json();
        showToast(data.error || 'Failed to save screen');
      }
    } catch (error) {
      showToast('Failed to save screen');
    }
  };

  const deleteScreen = async (screenId, title) => {
    if (!window.confirm(`Delete the screen "${title}"?`)) return;
    try {
      const response = await fetch(`${API_URL}/api/organizer/screens/${screenId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        if (liveDisplay && liveDisplay.type === 'screen' && liveDisplay.id === screenId) {
          setLiveDisplay(null);
        }
        fetchScreens(selectedQuizId);
      }
    } catch (error) {
      console.error('Error deleting screen:', error);
    }
  };

  const showScreenToTeams = (screen) => {
    if (!socket) {
      showToast('WebSocket not connected!');
      return;
    }
    socket.emit('organizer:showScreen', { quizId: selectedQuizId, screenId: screen.id });
    setLiveDisplay({ type: 'screen', id: screen.id });
    showToast(`Showing "${screen.title}" to teams 📺`);
  };

  const backToWaitingRoom = () => {
    if (!socket) {
      showToast('WebSocket not connected!');
      return;
    }
    socket.emit('organizer:hideScreen', { quizId: selectedQuizId });
    setLiveDisplay(null);
    showToast('Teams are back on the waiting screen');
  };

  const fetchHandIns = async (roundId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/organizer/quiz/${selectedQuizId}/round/${roundId}/hand-ins`
      );
      if (!response.ok) return;
      setHandIns(await response.json());
      setHandInsRoundId(roundId);
    } catch (error) {
      console.error('Error fetching hand-ins:', error);
    }
  };

  // Counts the answers of teams that never pressed Submit. Deliberately a
  // button and not a side effect of closing the round: a team that tapped one
  // option by accident shouldn't silently collect a point.
  const acceptDrafts = async (roundId, pendingCount) => {
    const confirmed = window.confirm(
      `Count the unsubmitted answers of ${pendingCount === 1 ? 'this team' : 'these teams'}?\n\n`
      + 'They will be scored exactly as if they had been handed in, and will show '
      + 'as "accepted" in the review and the CSV. This cannot be undone from here.'
    );
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_URL}/api/organizer/rounds/${roundId}/accept-drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (response.ok) {
        showToast(`Counted ${data.accepted} unsubmitted ${data.accepted === 1 ? 'answer' : 'answers'}`);
        fetchHandIns(roundId);
        fetchLeaderboard(selectedQuizId);
        if (gradingRoundId === roundId) fetchRoundAnswers(roundId);
      } else {
        showToast(data.error || 'Failed to accept answers');
      }
    } catch (error) {
      showToast('Failed to accept answers');
    }
  };

  const startEditTeam = (team) => {
    setEditingTeamId(team.id);
    setTeamNameDraft(team.team_name);
  };

  // Teams can't rename themselves and can't re-register under a new name, so
  // a typo at the door has to be fixable from here.
  const saveTeamName = async (teamId) => {
    const name = teamNameDraft.trim();
    if (!name) {
      showToast('A team needs a name');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/organizer/teams/${teamId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_name: name }),
      });
      const data = await response.json();
      if (response.ok) {
        setEditingTeamId(null);
        setTeamNameDraft('');
        fetchTeams(selectedQuizId);
        fetchLeaderboard(selectedQuizId);
        if (handInsRoundId) fetchHandIns(handInsRoundId);
      } else {
        showToast(data.error || 'Failed to rename team');
      }
    } catch (error) {
      showToast('Failed to rename team');
    }
  };

  // For ghost teams: a table that registered twice leaves one behind on zero
  // points, permanently showing as "not handed in".
  const removeTeam = async (team) => {
    const confirmed = window.confirm(
      `Remove "${team.team_name}"?\n\nTheir answers are deleted and they disappear from `
      + 'the leaderboard. If someone is still playing on that phone they will be sent '
      + 'back to the code screen. This cannot be undone.'
    );
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_URL}/api/organizer/teams/${team.id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        fetchTeams(selectedQuizId);
        fetchLeaderboard(selectedQuizId);
        if (handInsRoundId) fetchHandIns(handInsRoundId);
      } else {
        const data = await response.json();
        showToast(data.error || 'Failed to remove team');
      }
    } catch (error) {
      showToast('Failed to remove team');
    }
  };

  const createQuiz = async () => {
    if (!newQuizName.trim() || !newQuizCode.trim()) {
      showToast('Please enter both a quiz name and access code');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/organizer/quizzes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newQuizName, access_code: newQuizCode })
      });
      const data = await response.json();
      if (response.ok) {
        setNewQuizName('');
        setNewQuizCode('');
        setShowCreateQuiz(false);
        fetchQuizzes();
        setSelectedQuizId(data.quiz.id);
      } else {
        showToast(data.error || 'Failed to create quiz');
      }
    } catch (error) {
      showToast('Failed to create quiz');
    }
  };

  const deleteQuiz = async (quizId, quizName) => {
    if (!window.confirm(`Delete "${quizName}" and all its data? This cannot be undone.`)) return;
    try {
      const response = await fetch(`${API_URL}/api/organizer/quizzes/${quizId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        if (selectedQuizId === quizId) {
          setSelectedQuizId(null);
          setTeams([]);
          setRounds([]);
          setLeaderboard([]);
        }
        fetchQuizzes();
      }
    } catch (error) {
      console.error('Error deleting quiz:', error);
    }
  };

  const addRound = async () => {
    try {
      const response = await fetch(`${API_URL}/api/organizer/quiz/${selectedQuizId}/rounds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (response.ok) fetchRounds(selectedQuizId);
    } catch (error) {
      console.error('Error adding round:', error);
    }
  };

  const deleteRound = async (roundId) => {
    if (!window.confirm('Delete this round and all its questions?')) return;
    try {
      const response = await fetch(`${API_URL}/api/organizer/rounds/${roundId}`, {
        method: 'DELETE'
      });
      if (response.ok) fetchRounds(selectedQuizId);
    } catch (error) {
      console.error('Error deleting round:', error);
    }
  };

  const toggleRoundExpand = (roundId) => {
    setExpandedRoundIds((prev) =>
      prev.includes(roundId) ? prev.filter((id) => id !== roundId) : [...prev, roundId]
    );
  };

  const startEditRoundName = (round) => {
    setEditingRoundId(round.id);
    setRoundNameDraft(round.name || '');
  };

  const saveRoundName = async (roundId) => {
    try {
      const response = await fetch(`${API_URL}/api/organizer/rounds/${roundId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: roundNameDraft }),
      });
      if (response.ok) fetchRounds(selectedQuizId);
    } catch (error) {
      console.error('Error renaming round:', error);
    }
    setEditingRoundId(null);
    setRoundNameDraft('');
  };

  const moveRound = async (round, direction) => {
    const sorted = [...rounds].sort((a, b) => a.round_number - b.round_number);
    const index = sorted.findIndex((r) => r.id === round.id);
    const target = index + direction;
    if (target < 0 || target >= sorted.length) return;

    const reordered = [...sorted];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

    try {
      const response = await fetch(`${API_URL}/api/organizer/quiz/${selectedQuizId}/round-order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roundIds: reordered.map((r) => r.id) }),
      });
      if (response.ok) fetchRounds(selectedQuizId);
    } catch (error) {
      console.error('Error reordering rounds:', error);
    }
  };

  const startEditQuiz = () => {
    const quiz = quizzes.find((q) => q.id === selectedQuizId);
    setEditQuizName(quiz?.name || '');
    setEditQuizCode(quiz?.access_code || '');
    setEditQuizLanguage(quiz?.language === 'nl' ? 'nl' : 'en');
    setEditingQuiz(true);
  };

  const saveQuizEdit = async () => {
    if (!editQuizName.trim() || !editQuizCode.trim()) {
      showToast('Please enter both a quiz name and access code');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/organizer/quizzes/${selectedQuizId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editQuizName.trim(),
          access_code: editQuizCode.trim(),
          language: editQuizLanguage,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        showToast('Quiz updated!');
        setEditingQuiz(false);
        fetchQuizzes();
      } else {
        showToast(data.error || 'Failed to update quiz');
      }
    } catch (error) {
      showToast('Failed to update quiz');
    }
  };

  // mode: 'top3' shows the podium plus each team's own position;
  //       'all' shows the full standings.
  const showLeaderboardToTeams = (mode = 'top3') => {
    if (!socket) {
      showToast('WebSocket not connected!');
      return;
    }
    socket.emit('organizer:showLeaderboard', { quizId: selectedQuizId, mode });
    setLiveDisplay({ type: 'leaderboard', mode });
    showToast(mode === 'all' ? 'Full leaderboard shown to teams! 📣' : 'Top 3 shown to teams! 📣');
  };

  const copyRound = async (roundId) => {
    if (!copyTargetQuizId) return;
    try {
      const response = await fetch(`${API_URL}/api/organizer/rounds/${roundId}/copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetQuizId: copyTargetQuizId }),
      });
      const data = await response.json();
      if (response.ok) {
        showToast(`Copied ${data.questionsCopied} questions to "${data.targetQuizName}" as Round ${data.round.round_number}`);
        if (String(copyTargetQuizId) === String(selectedQuizId)) fetchRounds(selectedQuizId);
      } else {
        showToast(data.error || 'Copy failed');
      }
    } catch (error) {
      showToast('Copy failed');
    }
    setCopyingRoundId(null);
    setCopyTargetQuizId('');
  };

  const activateRound = async (roundId) => {
    if (!socket) {
      showToast('WebSocket not connected!');
      return;
    }
    socket.emit('organizer:activateRound', { roundId });
  };

  const closeRound = (roundId) => {
    if (!socket) {
      showToast('WebSocket not connected!');
      return;
    }
    socket.emit('organizer:closeRound', { roundId });
  };

  const reopenRound = (roundId) => {
    if (!socket) {
      showToast('WebSocket not connected!');
      return;
    }
    socket.emit('organizer:reopenRound', { roundId });
  };

  const resetQuiz = async () => {
    if (!window.confirm('⚠️ This will DELETE all teams, answers, and reset rounds. Are you sure?')) return;
    try {
      const response = await fetch(`${API_URL}/api/organizer/quiz/${selectedQuizId}/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        showToast('Quiz reset successfully!');
        fetchTeams(selectedQuizId);
        fetchRounds(selectedQuizId);
        fetchLeaderboard(selectedQuizId);
        setRoundAnswers([]);
        setGradingRoundId(null);
      }
    } catch (error) {
      console.error('Error resetting quiz:', error);
    }
  };

  const fetchRoundAnswers = async (roundId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/organizer/quiz/${selectedQuizId}/round/${roundId}/answers`
      );
      const data = await response.json();
      setRoundAnswers(data);
      setGradingRoundId(roundId);
    } catch (error) {
      console.error('Error fetching answers:', error);
    }
  };

  const gradeAnswer = async (answerId, isCorrect) => {
    try {
      await fetch(`${API_URL}/api/organizer/answers/${answerId}/grade`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_correct: isCorrect })
      });
      setRoundAnswers(prev =>
        prev.map(a => a.answer_id === answerId ? { ...a, is_correct: isCorrect ? 1 : 0 } : a)
      );
      fetchLeaderboard(selectedQuizId);
    } catch (error) {
      console.error('Error grading answer:', error);
    }
  };

  // ──────────────────────────────────────────────────────────
  // SPREADSHEET EXPORT
  // ──────────────────────────────────────────────────────────
  //
  // LEARNING POINT: This generates a CSV file entirely on the client.
  // No backend needed. We take the roundAnswers data (which is already
  // in memory) and convert it to a CSV string, then trigger a download.
  //
  // The trick: we create a Blob (binary data), make a temporary URL for it,
  // create an invisible <a> link, click it programmatically, then clean up.
  // This is the standard pattern for client-side file downloads in JS.
  //
  const exportToSpreadsheet = () => {
    if (roundAnswers.length === 0) {
      showToast('No answers to export');
      return;
    }

    // Pivot data: one row per team, one column per question
    // First, collect unique questions (in order) and unique teams
    const questionsMap = new Map(); // questionId -> { text, correctAnswer, type }
    const teamsMap = new Map();     // teamName -> { questionId -> { answer, result } }

    roundAnswers.forEach(a => {
      if (!questionsMap.has(a.question_id)) {
        questionsMap.set(a.question_id, {
          text: a.question_text,
          correct: a.correct_answer,
          type: a.question_type,
        });
      }
      if (!teamsMap.has(a.team_name)) {
        teamsMap.set(a.team_name, {});
      }

      const teamAnswer = formatSelectedAnswer(a);
      const result = a.is_correct === 1 ? 'Correct'
        : a.is_correct === 0 ? 'Wrong'
        : 'Pending';

      teamsMap.get(a.team_name)[a.question_id] = {
        answer: teamAnswer,
        result: a.question_type === 'open' ? result : `${result} (${formatScore(a.score)})`,
        score: Number(a.score || 0),
      };
    });

    const questionIds = [...questionsMap.keys()];
    const questionEntries = [...questionsMap.entries()];

    // Build headers: Team | Q1 Answer | Q1 Result | Q2 Answer | Q2 Result | ... | Total Correct
    const headers = ['Team'];
    questionEntries.forEach(([, q], i) => {
      // Truncate question text for header (max 40 chars)
      const label = q.text.length > 40 ? q.text.substring(0, 37) + '...' : q.text;
      headers.push(`Q${i + 1}: ${label}`);
      headers.push(`Q${i + 1} Result`);
    });
    headers.push('Total Correct');

    // Add a "Correct Answers" reference row at the top
    const correctRow = ['CORRECT ANSWERS'];
    questionEntries.forEach(([, q]) => {
      correctRow.push(q.correct);
      correctRow.push('—');
    });
    correctRow.push('—');

    // Build team rows
    const rows = [...teamsMap.entries()].map(([teamName, answers]) => {
      let correctCount = 0;
      const cells = [teamName];

      questionIds.forEach(qId => {
        const a = answers[qId];
        cells.push(a ? a.answer : '—');
        cells.push(a ? a.result : 'No answer');
        if (a) correctCount += a.score ?? (a.result === 'Correct' ? 1 : 0);
      });

      cells.push(formatScore(correctCount));
      return cells.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',');
    });

    const csv = [
      headers.map(h => `"${String(h).replace(/"/g, '""')}"`).join(','),
      correctRow.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','),
      ...rows
    ].join('\n');

    // Trigger download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const roundNum = rounds.find(r => r.id === gradingRoundId)?.round_number || 'unknown';
    const quizName = quizzes.find(q => q.id === selectedQuizId)?.name || 'quiz';
    link.download = `${quizName}_round${roundNum}_answers.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showToast('Spreadsheet downloaded! ✓');
  };

  // Also export the full leaderboard
  const exportLeaderboard = () => {
    if (leaderboard.length === 0) {
      showToast('No leaderboard data to export');
      return;
    }

    const headers = ['Rank', 'Team Name', 'Score', 'Total Answered'];
    const rows = leaderboard.map((team, index) => {
      return [index + 1, team.team_name, formatScore(team.score), team.total_answered]
        .map(val => `"${String(val).replace(/"/g, '""')}"`).join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const quizName = quizzes.find(q => q.id === selectedQuizId)?.name || 'quiz';
    link.download = `${quizName}_leaderboard.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showToast('Leaderboard exported! ✓');
  };

  if (loading) {
    return (
      <div style={s.container}>
        <p style={{ textAlign: 'center', fontSize: '18px', marginTop: '50px', color: colors.textMuted }}>
          Loading...
        </p>
      </div>
    );
  }

  // ==========================================
  // QUIZ SELECTOR VIEW
  // ==========================================
  if (!selectedQuizId) {
    return (
      <div style={s.container}>
        <div style={s.header}>
          <h1 style={s.title}>
            <img src="/logo.png" alt="Logo" style={{ height: '44px', width: 'auto', verticalAlign: 'middle', marginRight: '12px' }} />
            Organizer Dashboard
          </h1>
        </div>

        <div style={s.content}>
          <div style={s.section}>
            <div style={s.sectionHeader}>
              <h2 style={s.sectionTitle}>Your Quizzes</h2>
              <button onClick={() => setShowCreateQuiz(!showCreateQuiz)} style={s.addBtn}>
                + New Quiz
              </button>
            </div>

            {showCreateQuiz && (
              <div style={s.createForm}>
                <input
                  type="text"
                  placeholder="Quiz name (e.g. Friday Night Trivia)"
                  value={newQuizName}
                  onChange={(e) => setNewQuizName(e.target.value)}
                  style={commonStyles.input}
                />
                <input
                  type="text"
                  placeholder="Access code (e.g. FRIDAY1)"
                  value={newQuizCode}
                  onChange={(e) => setNewQuizCode(e.target.value.toUpperCase())}
                  style={commonStyles.input}
                  maxLength={20}
                />
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={createQuiz} style={s.saveBtn}>Create Quiz</button>
                  <button onClick={() => setShowCreateQuiz(false)} style={s.ghostBtn}>Cancel</button>
                </div>
              </div>
            )}

            <div style={s.quizGrid}>
              {quizzes.length === 0 ? (
                <p style={s.emptyText}>No quizzes yet. Create your first one!</p>
              ) : (
                quizzes.map((quiz) => (
                  <div key={quiz.id} style={s.quizCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <h3 style={s.quizCardName}>{quiz.name}</h3>
                      <span style={commonStyles.badgeOrange}>
                        {quiz.access_code}
                      </span>
                    </div>
                    <div style={s.quizCardStats}>
                      <span>{quiz.round_count || 0} rounds</span>
                      <span style={s.statDot}>·</span>
                      <span>{quiz.team_count || 0} teams</span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => setSelectedQuizId(quiz.id)}
                        style={s.openBtn}
                      >
                        Open
                      </button>
                      <button
                        onClick={() => deleteQuiz(quiz.id, quiz.name)}
                        style={s.deleteSmBtn}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {toast && <div style={commonStyles.toast}>{toast}</div>}
      </div>
    );
  }

  // ==========================================
  // QUIZ DASHBOARD VIEW
  // ==========================================
  const selectedQuiz = quizzes.find(q => q.id === selectedQuizId);

  return (
    <div style={s.container}>
      <div style={s.header}>
        <button onClick={() => setSelectedQuizId(null)} style={s.backBtn}>
          ← All Quizzes
        </button>
        {editingQuiz ? (
          <div style={s.quizEditForm}>
            <input
              type="text"
              value={editQuizName}
              onChange={(e) => setEditQuizName(e.target.value)}
              style={{ ...commonStyles.input, width: '220px' }}
              placeholder="Quiz name"
            />
            <input
              type="text"
              value={editQuizCode}
              onChange={(e) => setEditQuizCode(e.target.value.toUpperCase())}
              style={{ ...commonStyles.input, width: '160px' }}
              placeholder="Access code"
              maxLength={20}
            />
            {/* Language of everything TEAMS see. This dashboard stays English. */}
            <select
              value={editQuizLanguage}
              onChange={(e) => setEditQuizLanguage(e.target.value)}
              style={{ ...commonStyles.input, width: '150px', cursor: 'pointer' }}
              title="Language teams see"
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>{lang.label}</option>
              ))}
            </select>
            <button onClick={saveQuizEdit} style={s.saveBtn}>Save</button>
            <button onClick={() => setEditingQuiz(false)} style={s.ghostBtn}>Cancel</button>
          </div>
        ) : (
          <div>
            <h1 style={s.title}>
              {selectedQuiz?.name || 'Quiz'}
              <button onClick={startEditQuiz} style={s.editIconBtn} title="Edit name, password or language">✏️</button>
            </h1>
            <span style={{ color: colors.textMuted, fontSize: '14px' }}>
              Code: {selectedQuiz?.access_code}
              <span style={s.statDot}>·</span>
              Teams see: {selectedQuiz?.language === 'nl' ? 'Nederlands' : 'English'}
            </span>
          </div>
        )}
        <button onClick={resetQuiz} style={s.dangerBtn}>
          🔄 Reset
        </button>
      </div>

      <div style={s.content}>
        {/* Teams Section */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>👥 Teams ({teams.length})</h2>
          <div style={s.teamGrid}>
            {teams.length === 0 ? (
              <p style={s.emptyText}>No teams have joined yet...</p>
            ) : (
              teams.map((team) => (
                <div key={team.id} style={s.teamCard}>
                  {editingTeamId === team.id ? (
                    <>
                      <input
                        type="text"
                        value={teamNameDraft}
                        onChange={(e) => setTeamNameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveTeamName(team.id);
                          if (e.key === 'Escape') setEditingTeamId(null);
                        }}
                        style={{ ...commonStyles.input, padding: '8px 10px', fontSize: '14px' }}
                        autoFocus
                      />
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => saveTeamName(team.id)} style={s.teamActionBtn}>Save</button>
                        <button onClick={() => setEditingTeamId(null)} style={s.teamActionBtn}>Cancel</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '16px', fontWeight: '600' }}>{team.team_name}</span>
                      <span style={{ fontSize: '12px', color: colors.textDim }}>
                        {new Date(team.created_at).toLocaleTimeString()}
                      </span>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => startEditTeam(team)}
                          style={s.teamActionBtn}
                          title="Fix a typo in this team's name"
                        >
                          ✏️ Rename
                        </button>
                        <button
                          onClick={() => removeTeam(team)}
                          style={s.teamRemoveBtn}
                          title="Remove this team and its answers"
                        >
                          🗑️
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* ──────────────────────────────────────────────────────────
            SCREENS — what teams see when no round is running.
            Saved per quiz so the opening and closing text doesn't have to be
            retyped each night; a one-off message is a screen you show once.
            ────────────────────────────────────────────────────────── */}
        <div style={s.section}>
          <div style={s.sectionHeader}>
            <h2 style={s.sectionTitle}>📺 Screens</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={startNewScreen} style={s.addBtn}>+ New Screen</button>
              <button onClick={backToWaitingRoom} style={s.ghostBtn}>
                ↩ Back to waiting room
              </button>
            </div>
          </div>

          {editingScreenId !== null && (
            <div style={s.createForm}>
              <input
                type="text"
                placeholder="Screen title (e.g. Welcome, Break, Thanks for playing)"
                value={screenTitleDraft}
                onChange={(e) => setScreenTitleDraft(e.target.value)}
                style={commonStyles.input}
                autoFocus
              />
              <textarea
                placeholder="Message shown underneath (optional). Line breaks are kept."
                value={screenBodyDraft}
                onChange={(e) => setScreenBodyDraft(e.target.value)}
                style={{ ...commonStyles.input, minHeight: '90px', resize: 'vertical', fontFamily: 'inherit' }}
              />

              {/* Link buttons: socials, the night's playlist, a feedback form.
                  A bare domain is fine, https is assumed. */}
              <label style={s.screenFieldLabel}>Buttons (optional)</label>
              {screenLinksDraft.map((link, index) => (
                <div key={index} style={s.linkRow}>
                  <input
                    type="text"
                    placeholder="Button text (e.g. Our Instagram)"
                    value={link.label}
                    onChange={(e) => changeScreenLink(index, 'label', e.target.value)}
                    style={{ ...commonStyles.input, flex: '1 1 160px', padding: '10px 12px', fontSize: '14px' }}
                  />
                  <input
                    type="text"
                    placeholder="instagram.com/yourname"
                    value={link.url}
                    onChange={(e) => changeScreenLink(index, 'url', e.target.value)}
                    style={{ ...commonStyles.input, flex: '2 1 200px', padding: '10px 12px', fontSize: '14px' }}
                  />
                  {(link.label.trim() || link.url.trim()) && (
                    <button
                      type="button"
                      onClick={() => removeScreenLink(index)}
                      style={s.teamRemoveBtn}
                      title="Remove this button"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}

              {/* Counts down on every phone. Server-driven, so all tables
                  see the same number regardless of their clocks. */}
              <label style={s.screenFieldLabel}>Countdown (optional)</label>
              <div style={s.linkRow}>
                <input
                  type="number"
                  min="1"
                  placeholder="15"
                  value={screenCountdownDraft}
                  onChange={(e) => setScreenCountdownDraft(e.target.value)}
                  style={{ ...commonStyles.input, width: '110px', padding: '10px 12px', fontSize: '14px' }}
                />
                <span style={{ color: colors.textMuted, fontSize: '14px' }}>
                  minutes. Restarts each time you press Show. Leave empty for none.
                </span>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={saveScreen} style={s.saveBtn}>
                  {editingScreenId === 'new' ? 'Create Screen' : 'Save Screen'}
                </button>
                <button onClick={cancelScreenEdit} style={s.ghostBtn}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {screens.length === 0 ? (
              <p style={s.emptyText}>
                No screens yet. Add an opening, a break and an end screen and you can
                push any of them to every team with one click.
              </p>
            ) : (
              screens.map((screen) => {
                const isLive = liveDisplay && liveDisplay.type === 'screen' && liveDisplay.id === screen.id;
                return (
                  <div key={screen.id} style={{ ...s.roundCard, ...(isLive ? s.liveCard : {}) }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                      <h3 style={{ fontSize: '17px', margin: 0, fontWeight: '700' }}>{screen.title}</h3>
                      {isLive && <span style={commonStyles.badgeGreen}>LIVE</span>}
                      {screen.countdown_seconds ? (
                        <span style={commonStyles.badgeOrange}>
                          ⏱ {Math.round(screen.countdown_seconds / 60)} min
                        </span>
                      ) : null}
                      {screen.links && screen.links.length > 0 ? (
                        <span style={commonStyles.badgePurple}>
                          🔗 {screen.links.length}
                        </span>
                      ) : null}
                      {screen.body && (
                        <span style={s.screenPreview}>
                          {screen.body.length > 70 ? `${screen.body.slice(0, 67)}...` : screen.body}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => showScreenToTeams(screen)} style={s.purpleBtn}>
                        📣 Show
                      </button>
                      <button onClick={() => startEditScreen(screen)} style={s.activateBtn}>
                        Edit
                      </button>
                      <button onClick={() => deleteScreen(screen.id, screen.title)} style={s.deleteSmBtn}>
                        🗑️
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Rounds Section */}
        <div style={s.section}>
          <div style={s.sectionHeader}>
            <h2 style={s.sectionTitle}>📋 Rounds</h2>
            <button onClick={addRound} style={s.addBtn}>+ Add Round</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {rounds.length === 0 ? (
              <p style={s.emptyText}>No rounds yet. Add your first round!</p>
            ) : (
              [...rounds].sort((a, b) => a.round_number - b.round_number).map((round, roundIndex, sortedRounds) => {
              const isExpanded = expandedRoundIds.includes(round.id);
              const roundQuestions = allQuestions
                .filter((q) => q.round_id === round.id)
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.id - b.id);
              return (
                <div key={round.id} style={s.roundWrap}>
                <div style={s.roundCard}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button onClick={() => toggleRoundExpand(round.id)} style={s.expandBtn} title="Show questions">
                      {isExpanded ? '▼' : '▶'}
                    </button>
                    {editingRoundId === round.id ? (
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={roundNameDraft}
                          onChange={(e) => setRoundNameDraft(e.target.value)}
                          placeholder={`Round ${round.round_number} name`}
                          style={{ ...commonStyles.input, width: '200px', padding: '8px 10px' }}
                          autoFocus
                          onKeyPress={(e) => e.key === 'Enter' && saveRoundName(round.id)}
                        />
                        <button onClick={() => saveRoundName(round.id)} style={s.purpleBtn}>✓</button>
                        <button onClick={() => setEditingRoundId(null)} style={s.deleteSmBtn}>✕</button>
                      </div>
                    ) : (
                      <h3
                        style={{ fontSize: '18px', margin: 0, fontWeight: '700', cursor: 'pointer' }}
                        onClick={() => startEditRoundName(round)}
                        title="Click to rename this round"
                      >
                        Round {round.round_number}{round.name ? ` — ${round.name}` : ''}
                        <span style={{ fontSize: '13px', marginLeft: '8px', opacity: 0.6 }}>✏️</span>
                      </h3>
                    )}
                    <span style={{ color: colors.textMuted, fontSize: '14px' }}>
                      {round.question_count} questions
                    </span>
                    {round.is_active === 1 && !round.is_closed && (
                      <span style={commonStyles.badgeGreen}>ACTIVE</span>
                    )}
                    {round.is_closed === 1 && (
                      <span style={s.closedBadge}>CLOSED</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => moveRound(round, -1)}
                      style={s.deleteSmBtn}
                      title="Move round up"
                      disabled={roundIndex === 0}
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveRound(round, 1)}
                      style={s.deleteSmBtn}
                      title="Move round down"
                      disabled={roundIndex === sortedRounds.length - 1}
                    >
                      ↓
                    </button>
                    <button onClick={() => fetchHandIns(round.id)} style={s.activateBtn}>
                      ✅ Hand-ins
                    </button>
                    <button onClick={() => fetchRoundAnswers(round.id)} style={s.purpleBtn}>
                      📝 Review
                    </button>
                    {round.is_active === 1 && !round.is_closed && (
                      <button onClick={() => closeRound(round.id)} style={s.closeBtn}>
                        🔒 Close
                      </button>
                    )}
                    {round.is_closed === 1 && (
                      <button onClick={() => reopenRound(round.id)} style={s.reopenBtn}>
                        🔓 Reopen
                      </button>
                    )}
                    <button
                      onClick={() => activateRound(round.id)}
                      style={{
                        ...s.activateBtn,
                        ...(round.is_active === 1 && !round.is_closed ? s.activeBtnDisabled : {})
                      }}
                      disabled={round.is_active === 1 && !round.is_closed}
                    >
                      {round.is_active === 1 && !round.is_closed ? 'Active' : 'Activate'}
                    </button>
                    {copyingRoundId === round.id ? (
                      <>
                        <select
                          value={copyTargetQuizId}
                          onChange={(e) => setCopyTargetQuizId(e.target.value)}
                          style={s.copySelect}
                        >
                          <option value="">Copy to...</option>
                          {quizzes.map((q) => (
                            <option key={q.id} value={q.id}>{q.name}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => copyRound(round.id)}
                          style={s.purpleBtn}
                          disabled={!copyTargetQuizId}
                        >
                          ✓
                        </button>
                        <button
                          onClick={() => { setCopyingRoundId(null); setCopyTargetQuizId(''); }}
                          style={s.deleteSmBtn}
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <button onClick={() => setCopyingRoundId(round.id)} style={s.purpleBtn}>
                        📋 Copy
                      </button>
                    )}
                    <button onClick={() => deleteRound(round.id)} style={s.deleteSmBtn}>
                      🗑️
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div style={s.roundQuestionList}>
                    {roundQuestions.length === 0 ? (
                      <p style={s.emptyText}>No questions in this round yet.</p>
                    ) : (
                      roundQuestions.map((q, qi) => (
                        <div key={q.id} style={s.roundQuestionRow}>
                          <span style={s.roundQuestionLabel}>
                            {q.title ? q.title : `Q${qi + 1}`}
                          </span>
                          <span style={q.question_type === 'open' ? commonStyles.badgePurple : commonStyles.badgeOrange}>
                            {q.question_type === 'open' ? 'Open' : q.answer_mode === 'multi' ? 'Multi' : 'MC'}
                          </span>
                          {q.image_url && <span title="Has image">📷</span>}
                        </div>
                      ))
                    )}
                  </div>
                )}
                </div>
              );
              })
            )}
          </div>
        </div>

        {/* ──────────────────────────────────────────────────────────
            HAND-INS — who handed a round in, and who didn't.
            Closing a round does NOT count unsubmitted answers; they sit here
            as drafts until accepted, so a team that tapped one option by
            accident doesn't silently collect points.
            ────────────────────────────────────────────────────────── */}
        {handInsRoundId && (() => {
          const round = rounds.find((r) => r.id === handInsRoundId);
          const pending = handIns.filter((h) => h.status === 'drafting');
          const pendingAnswers = pending.reduce((sum, h) => sum + h.draft_count, 0);
          const isOpen = round && round.is_active === 1 && !round.is_closed;

          const label = {
            submitted: { text: 'Handed in', style: commonStyles.badgeGreen },
            accepted: { text: 'Accepted', style: commonStyles.badgePurple },
            drafting: {
              text: isOpen ? 'Still answering' : 'Not handed in',
              style: s.pendingBadge,
            },
            none: { text: 'Nothing yet', style: s.noneBadge },
          };

          return (
            <div style={s.section}>
              <div style={s.sectionHeader}>
                <h2 style={s.sectionTitle}>
                  ✅ Hand-ins — Round {round?.round_number}
                  {round?.name ? ` — ${round.name}` : ''}
                </h2>
                <button
                  onClick={() => { setHandInsRoundId(null); setHandIns([]); }}
                  style={s.ghostBtn}
                >
                  ✕ Close
                </button>
              </div>

              {pendingAnswers > 0 && (
                <div style={s.pendingNotice}>
                  <div>
                    <strong>
                      {pending.length} {pending.length === 1 ? 'team has' : 'teams have'} not handed in
                    </strong>
                    <div style={{ color: colors.textMuted, fontSize: '13px', marginTop: '4px' }}>
                      {isOpen
                        ? 'The round is still open, so they may yet hand in themselves.'
                        : `${pendingAnswers} ${pendingAnswers === 1 ? 'answer was' : 'answers were'} entered but never handed in. ${pendingAnswers === 1 ? 'It scores' : 'They score'} nothing until you accept ${pendingAnswers === 1 ? 'it' : 'them'}.`}
                    </div>
                  </div>
                  <button
                    onClick={() => acceptDrafts(handInsRoundId, pending.length)}
                    style={{ ...s.saveBtn, flex: '0 0 auto', padding: '12px 20px' }}
                  >
                    Accept {pendingAnswers} {pendingAnswers === 1 ? 'answer' : 'answers'}
                  </button>
                </div>
              )}

              <div style={s.tableContainer}>
                {handIns.length === 0 ? (
                  <p style={s.emptyText}>No teams in this quiz yet.</p>
                ) : (
                  <table style={s.table}>
                    <thead>
                      <tr style={s.tableHead}>
                        <th style={s.th}>Team</th>
                        <th style={s.th}>State</th>
                        <th style={s.th}>Answered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {handIns.map((row) => (
                        <tr key={row.team_id} style={s.tr}>
                          <td style={{ ...s.td, fontWeight: '600' }}>{row.team_name}</td>
                          <td style={s.td}>
                            <span style={label[row.status].style}>{label[row.status].text}</span>
                          </td>
                          <td style={{ ...s.td, color: colors.textMuted }}>
                            {row.answered} / {row.question_count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          );
        })()}

        {/* Answer Grading Section */}
        {gradingRoundId && (
          <div style={s.section}>
            <div style={s.sectionHeader}>
              <h2 style={s.sectionTitle}>
                📝 Answers — Round {rounds.find(r => r.id === gradingRoundId)?.round_number}
              </h2>
              <div style={{ display: 'flex', gap: '8px' }}>
                {/* SPREADSHEET EXPORT BUTTON */}
                <button onClick={exportToSpreadsheet} style={s.exportBtn}>
                  📊 Export CSV
                </button>
                <button onClick={() => { setGradingRoundId(null); setRoundAnswers([]); }} style={s.ghostBtn}>
                  ✕ Close
                </button>
              </div>
            </div>

            {roundAnswers.length === 0 ? (
              <p style={s.emptyText}>No answers submitted yet for this round.</p>
            ) : (
              (() => {
                const byQuestion = {};
                roundAnswers.forEach(a => {
                  if (!byQuestion[a.question_id]) {
                    byQuestion[a.question_id] = {
                      question_text: a.question_text,
                      question_type: a.question_type,
                        answer_mode: a.answer_mode,
                      correct_answer: a.correct_answer,
                      answers: []
                    };
                  }
                  byQuestion[a.question_id].answers.push(a);
                });

                return Object.entries(byQuestion).map(([qId, q]) => (
                  <div key={qId} style={s.gradingCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '8px' }}>
                      <strong style={{ fontSize: '16px' }}>{q.question_text}</strong>
                      <span style={q.question_type === 'open' ? commonStyles.badgePurple : commonStyles.badgeOrange}>
                        {q.question_type === 'open' ? 'Open' : q.answer_mode === 'multi' ? 'Multi' : 'MC'}
                      </span>
                    </div>
                    <p style={{ color: colors.success, fontSize: '13px', marginBottom: '12px', fontStyle: 'italic' }}>
                      Correct: {q.correct_answer}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {q.answers.map(a => (
                        <div key={a.answer_id} style={{
                          ...s.answerRow,
                          borderLeftColor: a.is_correct === 1 ? colors.success
                            : a.is_correct === 0 ? colors.error : colors.warning,
                        }}>
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: '700', minWidth: '100px' }}>{a.team_name}</span>
                            <span style={{ color: colors.textMuted }}>
                              {formatSelectedAnswer(a)}
                            </span>
                            {/* How this answer got here. A plain submission needs
                                no label; the other two do, because they score
                                differently or not at all. */}
                            {a.answer_status === 'draft' && (
                              <span style={s.pendingBadge} title="Entered but never handed in. Scores nothing until accepted.">
                                not counted
                              </span>
                            )}
                            {a.answer_status === 'accepted' && (
                              <span style={commonStyles.badgePurple} title="Was never handed in; you accepted it after the round closed.">
                                accepted
                              </span>
                            )}
                            {a.question_type !== 'open' && (
                              <span style={{ color: colors.textDim, fontSize: '12px' }}>
                                Score: {a.answer_status === 'draft' ? '—' : formatScore(a.score)}
                              </span>
                            )}
                          </div>
                          {q.question_type === 'open' ? (
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                onClick={() => gradeAnswer(a.answer_id, true)}
                                style={{
                                  ...s.gradeBtn,
                                  ...(a.is_correct === 1 ? s.gradeBtnCorrect : {}),
                                }}
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => gradeAnswer(a.answer_id, false)}
                                style={{
                                  ...s.gradeBtn,
                                  ...(a.is_correct === 0 ? s.gradeBtnWrong : {}),
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <span style={{
                              fontSize: '20px', fontWeight: '700',
                              color: a.is_correct === 1 ? colors.success : colors.error,
                            }}>
                              {a.is_correct === 1 ? '✓' : '✕'}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()
            )}
          </div>
        )}

        {/* Leaderboard Section */}
        <div style={s.section}>
          <div style={s.sectionHeader}>
            <h2 style={s.sectionTitle}>🏆 Leaderboard</h2>
            {leaderboard.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {/* Chosen per reveal rather than as a setting: early in the night
                    the podium keeps the tension, at the end everyone wants to
                    see where they landed. */}
                <button
                  onClick={() => showLeaderboardToTeams('top3')}
                  style={{
                    ...s.purpleBtn,
                    ...(liveDisplay?.type === 'leaderboard' && liveDisplay.mode === 'top3' ? s.liveBtn : {}),
                  }}
                >
                  📣 Show top 3
                </button>
                <button
                  onClick={() => showLeaderboardToTeams('all')}
                  style={{
                    ...s.purpleBtn,
                    ...(liveDisplay?.type === 'leaderboard' && liveDisplay.mode === 'all' ? s.liveBtn : {}),
                  }}
                >
                  📣 Show all teams
                </button>
                <button onClick={backToWaitingRoom} style={s.ghostBtn}>
                  ↩ Hide
                </button>
                <button onClick={exportLeaderboard} style={s.exportBtn}>
                  📊 Export CSV
                </button>
              </div>
            )}
          </div>
          <div style={s.tableContainer}>
            {leaderboard.length === 0 ? (
              <p style={s.emptyText}>No scores yet...</p>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr style={s.tableHead}>
                    <th style={s.th}>Rank</th>
                    <th style={s.th}>Team Name</th>
                    <th style={s.th}>Score</th>
                    <th style={s.th}>Answered</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((team, index) => (
                    <tr key={team.id} style={s.tr}>
                      <td style={s.td}>
                        <span style={{ fontSize: '20px' }}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                        </span>
                      </td>
                      <td style={{ ...s.td, fontWeight: '600' }}>{team.team_name}</td>
                      <td style={s.td}>
                        <strong style={{ fontSize: '22px', color: colors.primary }}>
                          {formatScore(team.score)}
                        </strong>
                      </td>
                      <td style={{ ...s.td, color: colors.textMuted }}>{team.total_answered}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {toast && <div style={commonStyles.toast}>{toast}</div>}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// STYLES
// ──────────────────────────────────────────────────────────
const s = {
  container: {
    ...commonStyles.pageContainer,
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '40px', flexWrap: 'wrap', gap: '15px',
    maxWidth: '1200px', margin: '0 auto 40px',
  },
  title: { fontSize: '28px', fontWeight: '800', margin: 0 },
  content: { maxWidth: '1200px', margin: '0 auto' },

  section: { marginBottom: '40px' },
  sectionHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '20px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '12px',
  },
  sectionTitle: { fontSize: '20px', fontWeight: '700', margin: 0 },

  // Buttons
  backBtn: {
    ...commonStyles.buttonGhost,
  },
  addBtn: {
    padding: '10px 20px', backgroundColor: colors.primary, color: '#fff',
    border: 'none', borderRadius: '8px', cursor: 'pointer',
    fontWeight: '700', fontSize: '14px',
  },
  saveBtn: {
    flex: 1, padding: '12px', backgroundColor: colors.success, color: '#fff',
    border: 'none', borderRadius: '8px', cursor: 'pointer',
    fontWeight: '700', fontSize: '15px',
  },
  ghostBtn: {
    ...commonStyles.buttonGhost,
  },
  dangerBtn: { ...commonStyles.buttonDanger },
  openBtn: {
    flex: 1, padding: '10px', backgroundColor: colors.primary, color: '#fff',
    border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '14px',
  },
  deleteSmBtn: {
    padding: '10px 14px', backgroundColor: colors.bgCard,
    border: `1px solid ${colors.error}`, borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', color: colors.text,
  },
  purpleBtn: {
    ...commonStyles.buttonSecondary,
  },
  copySelect: {
    padding: '8px 10px',
    backgroundColor: colors.bgInput,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    fontSize: '14px',
  },
  activateBtn: {
    padding: '10px 24px', fontSize: '14px', backgroundColor: colors.bgInput,
    color: colors.text, border: `1px solid ${colors.border}`,
    borderRadius: '8px', cursor: 'pointer', fontWeight: '600',
  },
  activeBtnDisabled: {
    opacity: 0.4, cursor: 'not-allowed',
  },
  closeBtn: {
    padding: '10px 20px', fontSize: '14px', backgroundColor: colors.warningMuted || 'rgba(243,156,18,0.15)',
    color: colors.warning, border: `1px solid ${colors.warning}`,
    borderRadius: '8px', cursor: 'pointer', fontWeight: '600',
  },
  reopenBtn: {
    padding: '10px 20px', fontSize: '14px', backgroundColor: colors.bgInput,
    color: colors.textMuted, border: `1px solid ${colors.border}`,
    borderRadius: '8px', cursor: 'pointer', fontWeight: '600',
  },
  closedBadge: {
    padding: '4px 10px', borderRadius: '6px', fontSize: '11px',
    fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase',
    backgroundColor: colors.errorMuted || 'rgba(231,76,60,0.15)',
    color: colors.error, border: `1px solid ${colors.error}`,
  },
  exportBtn: {
    padding: '8px 16px', backgroundColor: colors.successMuted, color: colors.success,
    border: `1px solid ${colors.success}`, borderRadius: '8px',
    cursor: 'pointer', fontWeight: '600', fontSize: '13px',
  },

  // Quiz grid
  quizGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '16px', marginTop: '16px',
  },
  quizCard: {
    backgroundColor: colors.bgCard, padding: '24px', borderRadius: '14px',
    display: 'flex', flexDirection: 'column', gap: '14px',
    border: `1px solid ${colors.border}`,
  },
  quizCardName: { fontSize: '18px', margin: 0, fontWeight: '700' },
  quizCardStats: { color: colors.textMuted, fontSize: '14px' },
  statDot: { margin: '0 8px' },

  // Create form
  createForm: {
    backgroundColor: colors.bgCard, padding: '24px', borderRadius: '14px',
    marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px',
    border: `1px solid ${colors.border}`,
  },

  // Teams
  teamGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '12px',
  },
  teamCard: {
    backgroundColor: colors.bgCard, padding: '16px', borderRadius: '10px',
    display: 'flex', flexDirection: 'column', gap: '4px',
    border: `1px solid ${colors.border}`,
  },

  // Rounds
  roundCard: {
    backgroundColor: colors.bgCard, padding: '18px 20px', borderRadius: '12px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    border: `1px solid ${colors.border}`, flexWrap: 'wrap', gap: '10px',
  },
  roundWrap: { display: 'flex', flexDirection: 'column' },
  liveCard: {
    border: `1px solid ${colors.success}`,  // shorthand, see QuestionDisplay optionSelected
    boxShadow: `0 0 0 1px ${colors.success}`,
  },
  liveBtn: {
    outline: `2px solid ${colors.success}`,
    outlineOffset: '2px',
  },
  teamActionBtn: {
    padding: '6px 10px', fontSize: '12px', fontWeight: '600',
    backgroundColor: colors.bgInput, color: colors.textMuted,
    border: `1px solid ${colors.border}`, borderRadius: '6px', cursor: 'pointer',
  },
  teamRemoveBtn: {
    padding: '6px 10px', fontSize: '12px', backgroundColor: colors.errorMuted,
    color: colors.error, border: `1px solid ${colors.error}`,
    borderRadius: '6px', cursor: 'pointer', flexShrink: 0,
  },
  screenFieldLabel: {
    color: colors.textMuted, fontSize: '12px', fontWeight: '700',
    textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px',
  },
  linkRow: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  pendingBadge: {
    backgroundColor: colors.warningMuted, color: colors.warning,
    padding: '4px 12px', borderRadius: '6px', fontSize: '12px',
    fontWeight: '700', letterSpacing: '0.5px',
  },
  noneBadge: {
    backgroundColor: colors.bgInput, color: colors.textDim,
    padding: '4px 12px', borderRadius: '6px', fontSize: '12px',
    fontWeight: '700', letterSpacing: '0.5px',
  },
  pendingNotice: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: '16px', flexWrap: 'wrap', marginBottom: '16px',
    padding: '16px 20px', borderRadius: '12px',
    backgroundColor: colors.warningMuted,
    border: `1px solid ${colors.warning}`,
  },
  screenPreview: {
    color: colors.textMuted, fontSize: '13px',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  expandBtn: {
    width: '28px', height: '28px', border: `1px solid ${colors.border}`,
    borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
    backgroundColor: 'transparent', color: colors.textMuted,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  roundQuestionList: {
    backgroundColor: colors.bgInput, borderRadius: '0 0 12px 12px',
    border: `1px solid ${colors.border}`, borderTop: 'none',
    padding: '10px 20px', marginTop: '-4px', display: 'flex',
    flexDirection: 'column', gap: '8px',
  },
  roundQuestionRow: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '8px 0', borderBottom: `1px solid ${colors.border}`,
  },
  roundQuestionLabel: { flex: 1, fontSize: '14px', color: colors.text },
  quizEditForm: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
  editIconBtn: {
    marginLeft: '10px', padding: '2px 6px', backgroundColor: 'transparent',
    border: 'none', cursor: 'pointer', fontSize: '16px', verticalAlign: 'middle',
  },

  // Grading
  gradingCard: {
    backgroundColor: colors.bgCard, padding: '20px', borderRadius: '12px',
    marginBottom: '12px', border: `1px solid ${colors.border}`,
  },
  answerRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 14px', backgroundColor: colors.bgInput, borderRadius: '8px',
    borderLeft: `4px solid ${colors.warning}`,
  },
  gradeBtn: {
    width: '36px', height: '36px', border: `1px solid ${colors.border}`,
    borderRadius: '8px', cursor: 'pointer', fontSize: '16px',
    backgroundColor: 'transparent', color: colors.text,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  gradeBtnCorrect: { backgroundColor: colors.success, border: `1px solid ${colors.success}` },
  gradeBtnWrong: { backgroundColor: colors.error, border: `1px solid ${colors.error}` },

  // Leaderboard
  tableContainer: {
    backgroundColor: colors.bgCard, borderRadius: '12px', padding: '20px',
    overflowX: 'auto', border: `1px solid ${colors.border}`,
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHead: { borderBottom: `2px solid ${colors.border}` },
  th: { padding: '12px', textAlign: 'left', color: colors.textMuted, fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' },
  tr: { borderBottom: `1px solid ${colors.border}` },
  td: { padding: '14px 12px' },

  emptyText: { color: colors.textDim, fontStyle: 'italic', padding: '20px 0' },
};

export default OrganizerDashboard;
